import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, Pencil, Trash2 } from 'lucide-react';
import {
  backLinkVariants,
  Button,
  Card,
  ConfirmDialog,
  DataTable,
  type DataTableColumn,
  EmptyState,
  Field,
  Input,
  PageBody,
  SectionLabel,
  TableActionsMenu,
} from '@wundo/ui';
import { ApiError } from '@wundo/client/api.ts';
import { PageHeader } from '@/components/PageHeader.tsx';
import { TopicDialog } from '@/components/topics/TopicDialog.tsx';
import {
  createTopic,
  deleteTopic,
  getTopicSet,
  type TopicInput,
  updateTopic,
  updateTopicSet,
} from '@/lib/topicSets.ts';
import type { Topic, TopicSet } from '@/types/topicSets.ts';
import { apiErrorMessage } from '@/lib/apiErrorMessage.ts';
import { ReadingColumn } from '@/components/ReadingColumn.tsx';
import { useRecordOrganisation } from '@/lib/recordOrganisation.ts';

export const Route = createFileRoute('/_app/topics/$id')({
  staticData: { orgScope: 'record', orgScopeList: '/topics' },
  component: TopicSetDetailRoute,
});

function TopicSetDetailRoute() {
  return <TopicSetDetail id={Route.useParams().id} />;
}

const columns: DataTableColumn<Topic>[] = [
  { key: 'title', header: 'Title', width: 'w-1/3' },
  {
    key: 'description',
    header: 'Description',
    render: (topic) => <span className='line-clamp-2'>{topic.description}</span>,
  },
];

/** One topic set: its name, and the themes a track built on it offers. */
export function TopicSetDetail({ id }: { id: string }) {
  const queryClient = useQueryClient();

  const {
    data: set,
    isPending,
    error,
  } = useQuery({
    queryKey: ['topicSet', id],
    queryFn: () => getTopicSet(id),
  });
  useRecordOrganisation(set?.organisation_id);

  const [editing, setEditing] = useState<Topic | null>(null);
  const [topicOpen, setTopicOpen] = useState(false);
  const [topicError, setTopicError] = useState('');

  const [removingTopic, setRemovingTopic] = useState<Topic | null>(null);

  const saveTopic = useMutation({
    mutationFn: (input: TopicInput) =>
      editing ? updateTopic(id, editing.id, input) : createTopic(id, input),
    onSuccess: async () => {
      setTopicOpen(false);
      await queryClient.invalidateQueries({ queryKey: ['topicSet', id] });
    },
    onError: (failure) => setTopicError(apiErrorMessage(failure, 'Failed to save topic')),
  });

  if (error instanceof ApiError && error.status === 404) {
    return (
      <>
        <BackToList />
        <PageHeader title='Topic set' />
        <PageBody className='mt-3.5'>
          <Card>
            <EmptyState
              title='Topic set not found'
              description='It may have been deleted.'
            />
          </Card>
        </PageBody>
      </>
    );
  }

  if (error) {
    throw error;
  }

  function openTopic(topic: Topic | null) {
    setEditing(topic);
    setTopicError('');
    setTopicOpen(true);
  }

  return (
    <>
      <BackToList />
      <PageHeader title={set?.name ?? 'Topic set'} />
      <PageBody className='mt-3.5'>
        <div className='flex flex-col gap-4'>
          {/* One field: a column, where the table below it is the page. */}
          <ReadingColumn>
            <TopicSetNameCard set={set} />
          </ReadingColumn>

          <section className='mt-2'>
            <div className='mb-3 flex items-center justify-between gap-3'>
              <SectionLabel as='h2'>Topics ({set?.topics.length ?? 0})</SectionLabel>
              <Button size='sm' onClick={() => openTopic(null)}>
                Add topic
              </Button>
            </div>
            <DataTable
              label='Topics'
              columns={columns}
              rows={set?.topics ?? []}
              rowKey={(topic) => topic.id}
              loading={isPending}
              onRowClick={(topic) => openTopic(topic)}
              emptyState={
                <EmptyState
                  title='No topics yet'
                  description='Click "Add topic" to start.'
                  headingLevel='h3'
                />
              }
              actions={(topic) => (
                <TableActionsMenu
                  label={`Actions for ${topic.title}`}
                  items={[
                    {
                      label: 'Edit',
                      icon: <Pencil aria-hidden='true' />,
                      onSelect: () => openTopic(topic),
                    },
                    {
                      label: 'Delete',
                      icon: <Trash2 aria-hidden='true' />,
                      tone: 'danger',
                      onSelect: () => setRemovingTopic(topic),
                    },
                  ]}
                />
              )}
            />
          </section>
        </div>
      </PageBody>

      <TopicDialog
        open={topicOpen}
        onOpenChange={(open) => {
          setTopicOpen(open);
          if (!open) setTopicError('');
        }}
        topic={editing}
        saving={saveTopic.isPending}
        error={topicError}
        onSave={(input) => {
          setTopicError('');
          saveTopic.mutate(input);
        }}
      />

      <DeleteTopicDialog
        setId={id}
        topic={removingTopic}
        onClose={() => setRemovingTopic(null)}
      />
    </>
  );
}

/* The way back, in the gap PageBody leaves above its first child. */
function BackToList() {
  return (
    <Link to='/topics' className={backLinkVariants({ className: 'mb-2.5' })}>
      <ChevronLeft aria-hidden='true' />
      Topic sets
    </Link>
  );
}

/**
 * A topic set's name, saved on blur: it is the only field on the page, and a
 * form around a single input is a button nobody would think to press.
 */
function TopicSetNameCard({
  set,
}: {
  /** The set once loaded; the field waits for it. */
  set: TopicSet | undefined;
}) {
  const queryClient = useQueryClient();
  const [nameDraft, setNameDraft] = useState<string | null>(null);
  const [renameError, setRenameError] = useState('');

  const rename = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      updateTopicSet(id, { name }),
    onSuccess: async (_saved, { id }) => {
      setNameDraft(null);
      await queryClient.invalidateQueries({ queryKey: ['topicSet', id] });
      await queryClient.invalidateQueries({ queryKey: ['topicSets'] });
    },
    /* The field goes back to the stored name: what is on screen is what is saved. */
    onError: (failure) => {
      setRenameError(apiErrorMessage(failure, 'Failed to rename'));
      setNameDraft(null);
    },
  });

  return (
    <Card>
      <Field label='Name'>
        {(control) => (
          <Input
            {...control}
            value={nameDraft ?? set?.name ?? ''}
            disabled={set === undefined}
            onChange={(event) => setNameDraft(event.target.value)}
            onBlur={() => {
              if (!set || nameDraft === null) return;
              const next = nameDraft.trim();
              /* Nothing to save: an empty box and an unchanged one both mean "leave it". */
              if (next === '' || next === set.name) {
                setNameDraft(null);
                return;
              }
              setRenameError('');
              rename.mutate({ id: set.id, name: next });
            }}
          />
        )}
      </Field>
      {renameError !== '' && (
        <p role='alert' className='mt-2 text-sm text-danger'>
          {renameError}
        </p>
      )}
      {set?.organisation_id === null && (
        <p className='mt-2 text-xs text-ink-muted'>
          This is the global default. Tracks fall back to it when no organisation-specific
          topic set is linked.
        </p>
      )}
    </Card>
  );
}

interface DeleteTopicDialogProps {
  setId: string;
  /** The topic to delete; null keeps the dialog closed. */
  topic: Topic | null;
  onClose: () => void;
}

function DeleteTopicDialog({ setId, topic, onClose }: DeleteTopicDialogProps) {
  const queryClient = useQueryClient();
  const [error, setError] = useState('');

  const removeTopic = useMutation({
    mutationFn: (doomed: Topic) => deleteTopic(setId, doomed.id),
    onSuccess: async () => {
      onClose();
      await queryClient.invalidateQueries({ queryKey: ['topicSet', setId] });
    },
    onError: (failure) => setError(apiErrorMessage(failure, 'Failed to delete topic')),
  });

  return (
    <ConfirmDialog
      open={topic !== null}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
          setError('');
        }
      }}
      title='Delete topic?'
      description={`Delete "${topic?.title ?? ''}"?`}
      confirmLabel='Delete'
      cancelLabel='Cancel'
      variant='danger'
      error={error}
      onConfirm={async () => {
        if (!topic) return;
        setError('');
        await removeTopic.mutateAsync(topic).catch(() => undefined);
      }}
    />
  );
}
